/**
 * Achievement localization resolver — the single key-based path shared by the
 * Achievements screen and the unlock toast (and any future achievement
 * presentation).
 *
 * Resolution order (approved Phase 5 product decision):
 *   1. Known stable backend `key` → frontend-owned localized name/description
 *      from the `achievements` catalog.
 *   2. Unknown future key → the backend's English name/description verbatim.
 *   3. Missing key and missing backend prose → localized generic fallback.
 *
 * Achievements are NEVER identified by comparing or parsing English prose —
 * the stable `key` (present in both GET /api/v1/achievements and the
 * `achievement_unlocked` broadcast) is the only identity.
 */
import { t } from './index';
import enAchievements from './locales/en/achievements.json';

type CatalogKey = keyof typeof enAchievements.catalog;

const KNOWN_KEYS: ReadonlySet<string> = new Set(Object.keys(enAchievements.catalog));

/** The stable keys the frontend catalog covers (test hook + parity checks). */
export function knownAchievementKeys(): readonly string[] {
  return [...KNOWN_KEYS];
}

/** Minimal prose shape shared by the REST payload and the unlock event. */
export interface AchievementProse {
  key: string;
  name?: string | null;
  description?: string | null;
}

function isKnownKey(key: string): key is CatalogKey {
  return KNOWN_KEYS.has(key);
}

export function localizedAchievementName(a: AchievementProse): string {
  if (isKnownKey(a.key)) return t(`achievements:catalog.${a.key}.name`);
  if (a.name) return a.name;
  return t('achievements:fallback.name');
}

export function localizedAchievementDescription(a: AchievementProse): string {
  if (isKnownKey(a.key)) return t(`achievements:catalog.${a.key}.description`);
  if (a.description) return a.description;
  return t('achievements:fallback.description');
}

/**
 * Complete unlock-toast sentence, resolved at event time in the active
 * language. Unknown-key names pass through as plain text — SocialToastManager
 * renders React text, so hostile backend prose cannot inject markup.
 */
export function achievementUnlockedToastText(a: AchievementProse): string {
  return t('achievements:toast.unlocked', { name: localizedAchievementName(a) });
}
